import React from 'react';
import { Toaster } from 'react-hot-toast';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
// Dashboard removed - Patients is the product (see pitch doc)
import LoginPage from './components/LoginPage';
import Patients from './pages/PatientsTriage';
import PatientsEngaged from './pages/PatientsEngaged';
import Settings from './pages/Settings';
import Rules from './pages/Rules';
import Campaigns from './pages/Campaigns';
import CampaignDetail from './pages/CampaignDetail';
import CampaignBuilderPage from './components/campaigns/CampaignBuilderPage';
import ImportHistory from './pages/ImportHistory';
import { AuthProvider, useAuth } from './components/context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import SuperAdminRoute from './components/SuperAdminRoute';
import NotFound from './pages/NotFound'; // General NotFound page
// You might want a specific PublicNotFound page if its layout should differ
// import PublicNotFound from './pages/PublicNotFound';
import ResetPassword from './pages/ResetPassword';
import ForgotPassword from './pages/ForgotPassword';
import AppLayout from './components/layout/AppLayout';
import SuperAdminLayout from './components/layout/SuperAdminLayout';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import SecureMessageViewer from './pages/SecureMessageViewer';

// A simple loading component, you can replace this with a more sophisticated spinner
function GlobalLoadingIndicator() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#f8fafc' }}>
      <div style={{ width: '2rem', height: '2rem', border: '2px solid #e2e8f0', borderTopColor: '#0f172a', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function AppRoutes() {
  const { loading, user, isSuperAdmin } = useAuth();

  if (loading) {
    // Render a global loading indicator or a blank page until auth is resolved
    return <GlobalLoadingIndicator />;
  }

  // Super-admins go to /admin, clinic users go to /patients
  const postLoginRedirect = isSuperAdmin ? '/admin' : '/patients';

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={user ? <Navigate to={postLoginRedirect} replace /> : <LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/secure-message/:token" element={<SecureMessageViewer />} />

      {/* Super-admin routes */}
      <Route element={<SuperAdminRoute><SuperAdminLayout /></SuperAdminRoute>}>
        <Route path="/admin" element={<SuperAdminDashboard />} />
        <Route path="/admin/clinics/:clinicId" element={<SuperAdminDashboard />} />
      </Route>

      {/* Protected routes wrapped by ProtectedRoute which handles AppLayout */}
      <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
        {/* Default route - Patients is the product */}
        <Route index element={<Navigate to="/patients" replace />} />
        <Route path="/dashboard" element={<Navigate to="/patients" replace />} />
        <Route path="/patients" element={<Patients />} />
        <Route path="/patients/engaged" element={<PatientsEngaged />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/automations" element={<Rules />} />
        <Route path="/rules" element={<Navigate to="/automations" replace />} />
        <Route path="/campaigns" element={<Campaigns />} />
        <Route path="/campaigns/new" element={<CampaignBuilderPage />} />
        <Route path="/campaigns/:id" element={<CampaignDetail />} />
        <Route path="/import-history" element={<ImportHistory />} />
        {/* Catch-all 404 for authenticated users (renders inside AppLayout) */}
        <Route path="*" element={<NotFound />} />
      </Route>

      {/* Fallback for any routes not matched above (e.g., unauthenticated user hitting a random URL) */}
      {/* This NotFound should ideally not use AppLayout */}
      <Route path="*" element={<NotFound />} />
      {/* If you have a specific PublicNotFound page:
      <Route path="*" element={<PublicNotFound />} />
      */}
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <Toaster
          position="top-center"
          toastOptions={{
            style: {
              borderRadius: '8px',
              background: '#ffffff',
              color: '#333',
              fontSize: '14px',
              boxShadow: '0 4px 10px rgba(0, 0, 0, 0.05)',
              padding: '12px 16px',
            },
            success: {
              iconTheme: {
                primary: '#11999E',
                secondary: '#ECFDF5',
              },
            },
          }}
        />
        <AppRoutes />
      </AuthProvider>
    </Router>
  );
}

export default App;
